import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';
import i18n from '@/i18n';
import api from '@/services/api';
import { preferencesAPI } from '@/api/preferences';
import { isAuthenticated, getPortalType } from '@/utils/auth';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [arabicEnabled, setArabicEnabled] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('userportal_config') || '{}');
      return cached.arabic_language_support_enabled === true;
    } catch (_) {
      return false;
    }
  });

  const [language, setLanguageState] = useState(() => {
    let isFeatureEnabled = false;
    try {
      const cached = JSON.parse(localStorage.getItem('userportal_config') || '{}');
      isFeatureEnabled = cached.arabic_language_support_enabled === true;
    } catch (_) {}

    // If Arabic support is not explicitly enabled, default strictly to English
    if (!isFeatureEnabled) {
      return 'en';
    }

    const current = i18n.language || localStorage.getItem('app-language') || 'en';
    return current.startsWith('ar') ? 'ar' : 'en';
  });

  const isRTL = arabicEnabled && language === 'ar';
  const direction = isRTL ? 'rtl' : 'ltr';

  const applyDocumentAttributes = useCallback((lang, enabled = true) => {
    const isAr = enabled && lang === 'ar';
    const dir = isAr ? 'rtl' : 'ltr';
    const activeLang = isAr ? 'ar' : 'en';
    document.documentElement.lang = activeLang;
    document.documentElement.dir = dir;
    if (document.body) {
      document.body.dir = dir;
    }
  }, []);

  const changeLanguage = useCallback(async (newLang) => {
    // Prevent switching to Arabic if feature flag is disabled
    if (!arabicEnabled && newLang === 'ar') {
      return;
    }

    const normalized = newLang === 'ar' ? 'ar' : 'en';
    if (normalized === language) return;

    try {
      await i18n.changeLanguage(normalized);
      localStorage.setItem('app-language', normalized);
      setLanguageState(normalized);
      applyDocumentAttributes(normalized, arabicEnabled);

      // Silently sync with backend preferences if user is authenticated
      try {
        const portal = getPortalType(window.location.pathname || '');
        if (isAuthenticated(portal)) {
          preferencesAPI.updatePreferences({ preferred_languages: [normalized] }).catch(() => {});
        }
      } catch (_) {}
    } catch (err) {
      console.error('Failed to change language:', err);
    }
  }, [language, arabicEnabled, applyDocumentAttributes]);

  // Sync portal feature flags for Arabic language support
  useEffect(() => {
    let isMounted = true;

    const syncPortalConfig = async () => {
      try {
        const response = await api.get('/userportal/config/');
        const features = response?.data?.features || {};
        const isEnabled = features.arabic_language_support_enabled === true;

        if (!isMounted) return;
        setArabicEnabled(isEnabled);

        try {
          localStorage.setItem('userportal_config', JSON.stringify(features));
        } catch (_) {}

        // If Arabic is disabled in SuperAdmin, enforce English and LTR immediately
        if (!isEnabled) {
          if (i18n.language !== 'en' || localStorage.getItem('app-language') === 'ar') {
            await i18n.changeLanguage('en');
            localStorage.setItem('app-language', 'en');
            setLanguageState('en');
            applyDocumentAttributes('en', false);
          }
        }
      } catch (err) {
        console.warn('[LanguageProvider] Failed to fetch portal config for language support:', err);
      }
    };

    syncPortalConfig();

    const handleConfigUpdated = (e) => {
      const features = e.detail || {};
      const isEnabled = features.arabic_language_support_enabled === true;
      setArabicEnabled(isEnabled);
      if (!isEnabled) {
        i18n.changeLanguage('en');
        localStorage.setItem('app-language', 'en');
        setLanguageState('en');
        applyDocumentAttributes('en', false);
      }
    };

    window.addEventListener('userportal_config_updated', handleConfigUpdated);
    window.addEventListener('focus', syncPortalConfig);

    return () => {
      isMounted = false;
      window.removeEventListener('userportal_config_updated', handleConfigUpdated);
      window.removeEventListener('focus', syncPortalConfig);
    };
  }, [applyDocumentAttributes]);

  // Keep state in sync if i18next language changes externally
  useEffect(() => {
    applyDocumentAttributes(language, arabicEnabled);

    const handleLanguageChanged = (lng) => {
      if (!arabicEnabled) {
        setLanguageState('en');
        applyDocumentAttributes('en', false);
        return;
      }
      const normalized = lng && lng.startsWith('ar') ? 'ar' : 'en';
      setLanguageState(normalized);
      applyDocumentAttributes(normalized, true);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [language, arabicEnabled, applyDocumentAttributes]);

  const value = {
    language,
    direction,
    isRTL,
    arabicEnabled,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      <DirectionProvider dir={direction}>
        {children}
      </DirectionProvider>
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
